'use client';

import Image from 'next/image';
import React from 'react';
import { LandingButton } from '../shared';

interface FeatureShowcaseProps {
  title: string;
  description: string;
  imageSrc: string;
  imageAlt: string;
  ctaText: string;
  ctaHref: string;
  reversed?: boolean;
  className?: string;
}

export function FeatureShowcase({
  title,
  description,
  imageSrc,
  imageAlt,
  ctaText,
  ctaHref,
  reversed = false,
  className = '',
}: FeatureShowcaseProps) {
  return (
    <section className={`py-16 md:py-24 px-4 md:px-6 ${className}`}>
      <div className="max-w-6xl mx-auto">
        <div
          className={`grid md:grid-cols-2 gap-8 md:gap-12 items-center ${reversed ? 'md:flex-row-reverse' : ''}`}
        >
          <div className={`${reversed ? 'md:order-2' : ''}`}>
            <h2 className="text-fluid-3xl md:text-fluid-4xl font-bold text-neutral-900 mb-4 leading-tight">
              {title}
            </h2>
            <p className="text-fluid-lg text-neutral-600 leading-relaxed mb-6">{description}</p>
            <LandingButton href={ctaHref} showArrow>
              {ctaText}
            </LandingButton>
          </div>

          <div className={`${reversed ? 'md:order-1' : ''}`}>
            <div className="relative bg-neutral-100 rounded-xl border border-neutral-200 overflow-hidden h-80 md:h-96">
              <Image src={imageSrc} alt={imageAlt} fill className="object-contain" unoptimized />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

interface ConsolidationShowcaseProps {
  className?: string;
}

export function ConsolidationShowcase({ className = '' }: ConsolidationShowcaseProps) {
  return (
    <FeatureShowcase
      title="Konsolidasi: Selamat tinggal tradisi bolak-balik baca perubahan"
      description="Berhenti membuang waktu menyusun puzzle pasal yang diubah, dicabut, atau disisipkan. Justisio otomatis menyatukan semua riwayat perubahan ke dalam satu naskah konsolidasi utuh yang bersih dan siap dibaca."
      imageSrc="/public-assets/assets/248030d1-9097-4c36-92a9-7725d9eeca70.png"
      imageAlt="Konsolidasi Justisio"
      ctaText="Lihat Contoh Konsolidasi"
      ctaHref="/id/home"
      className={`bg-white ${className}`}
    />
  );
}

interface ContextSearchShowcaseProps {
  className?: string;
}

export function ContextSearchShowcase({ className = '' }: ContextSearchShowcaseProps) {
  return (
    <FeatureShowcase
      title="Cari aturan pakai konteks, bukan tebak-tebakan keyword"
      description="Tinggalkan pencarian kaku berbasis kata kunci. Ketik konteks masalah Anda, dan dalam 1 detik pasal yang relevan akan muncul. Kami melakukan parsing hingga tingkat pasal sehingga Anda dapat membaca dan menyalinnya dengan rapi."
      imageSrc="/public-assets/assets/392e4bc8-2e01-44a8-985d-d10410dd311a.png"
      imageAlt="Pencarian konteks Justisio"
      ctaText="Coba Cari Konteks Gratis"
      ctaHref="/id/home"
      reversed
      className={`bg-neutral-50 ${className}`}
    />
  );
}

interface RelationsShowcaseProps {
  className?: string;
}

export function RelationsShowcase({ className = '' }: RelationsShowcaseProps) {
  return (
    <section className={`py-16 md:py-24 px-4 md:px-6 bg-white ${className}`}>
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-12">
          <h2 className="text-fluid-3xl md:text-fluid-4xl font-bold text-neutral-900 mb-4">
            Relasi aturan secara gamblang
          </h2>
          <p className="text-fluid-lg text-neutral-600 max-w-2xl mx-auto">
            Hukum tidak berdiri sendiri. Di Justisio, Anda bisa dengan mudah melihat gambaran besar
            sebuah aturan: apa aturan turunannya, aturan mana yang mengubahnya, dan aturan mana yang
            mencabutnya.
          </p>
        </div>

        <div className="flex flex-col items-center">
          <div className="w-full max-w-4xl bg-neutral-50 border-2 border-neutral-200 rounded-xl p-8 md:p-12">
            <div className="flex flex-col items-center gap-8">
              <div className="bg-neutral-900 text-white px-6 py-3 rounded-lg font-bold shadow-[4px_4px_0px_0px_rgba(150,150,150,1)]">
                UU Cipta Kerja
              </div>

              <div className="flex flex-col items-center">
                <div className="w-0.5 h-8 bg-neutral-300" />
                <div className="w-2 h-2 bg-neutral-300 rounded-full" />
              </div>

              <div className="flex flex-wrap justify-center gap-4">
                {['PP 35/2021', 'PP 36/2021', 'PP 37/2021'].map((reg) => (
                  <div
                    key={reg}
                    className="bg-white border-2 border-neutral-900 px-4 py-2 rounded-lg font-semibold text-sm shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] hover:shadow-[4px_4px_0px_0px_rgba(0,0,0,1)] hover:-translate-y-0.5 transition-all duration-150 cursor-pointer"
                  >
                    {reg}
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8">
            <LandingButton href="/id/home" showArrow>
              Jelajahi Hierarki Hukum
            </LandingButton>
          </div>
        </div>
      </div>
    </section>
  );
}

interface FocusModeShowcaseProps {
  className?: string;
}

export function FocusModeShowcase({ className = '' }: FocusModeShowcaseProps) {
  return (
    <FeatureShowcase
      title="Pahami rute keterkaitan antar pasal tanpa tersesat"
      description="Sering pusing karena sebuah pasal berkaitan dengan banyak pasal-pasal lain? Tinggal masuk ke Mode Fokus, Anda bisa langsung melihat rute pasal tersebut terkait dengan pasal apa saja, dan pasal mana saja yang merujuknya, rapi dan super nyaman."
      imageSrc="/public-assets/assets/aaed95ff-48d5-4d89-be07-db68ad5c0762.png"
      imageAlt="Mode Fokus Justisio"
      ctaText="Coba Mode Fokus"
      ctaHref="/id/home"
      className={`bg-neutral-50 ${className}`}
    />
  );
}
